import React, { useEffect, useState } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/invoiceTable.css";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../components/ReLoginModal";
import { CircularProgress } from "@mui/material";
import api from "../util/axios";

type Requisition = {
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  price: any;
  quantity: string;
  _id: string;
  loadingSlip: any;
  requisitions: any;
  approvalNumber: string;
  customer?: { businessName: string };
  items?: { name: string; unitPrice: number; quantity: number; volume: number; totalCost: number }[];
  updatedAt: string;
  status: string;
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const LoadingSlipTable = () => {
  const navigate = useNavigate();
  const [reLogin, setReLogin] = useState(false);
  const [getRequisition, setGetRequisition] = useState<Requisition[]>([]);
  const [selectedRequisitions, setSelectedRequisitions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchQuery, setSearchQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");

  const fetchRequisitionData = async () => {
    setIsLoading(true);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      setIsLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await api.get(
        "/requisition", 
        { headers }
      );
      const approvedRequisitions = response.data.data.requisitions.filter(
        (requisition: Requisition) => requisition.status === "APPROVED"
      );
      setGetRequisition(approvedRequisitions);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch requisitions";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
         
        setReLogin(true);
      } else {
        setReLogin(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitionData();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateInvoice = (datas: Requisition) => {
    navigate("/dashboard/loadingslip", { state: datas });
  };

  const handleSelectRequisition = (id: string) => {
    setSelectedRequisitions((prev) =>
      prev.includes(id) ? prev.filter((reqId) => reqId !== id) : [...prev, id]
    );
  };

  const handleMergeRequisitions = () => {
    if (selectedRequisitions.length === 0) {
      toast.error("Please select at least one requisition to merge.");
      return;
    }

    else if (selectedRequisitions.length < 2) {
      toast.error("Please select at least two requisitions to merge.");
      return;
    }
  
    const selectedData = getRequisition.filter((req) => selectedRequisitions.includes(req._id));
    // console.log("Selected data:", selectedData);
    const customerNames = [
      ...new Set(
        selectedData
          .map((req) => req.customer?.businessName)
          .filter((name): name is string => name !== undefined)
      ),
    ];
    
    if (customerNames.length > 1) {
      toast.error("Selected requisitions must belong to the same customer.");
      return;
    }
    const productNames = [
      ...new Set(
        selectedData
          .flatMap((req) => (req.items || []).map((item: any) => item.name))
          .filter((name): name is string => name !== undefined)
      ),
    ];
    if (productNames.length > 1) {
      toast.error("Selected requisitions must be the same product");
      return;
    }

    const mergedApprovalNumbers = selectedData.map((req) => req.approvalNumber);
  
    const mergedItems = selectedData.flatMap((req) =>
      (req.items || []).map((item: any) => ({
        ...item,
        requisitionId: req._id, 
      }))
    );
    const mergedRequisition = {
      selectedData: selectedData,
      items: mergedItems,
      mergedIds: selectedRequisitions,
      customer: selectedData[0].customer,
      approvalNumbers: mergedApprovalNumbers 
    };
    navigate("/dashboard/mergedloadingslip", { state: mergedRequisition });
  };

  const calculateTotalQuantity = (data: Requisition[]) => {
    return data.reduce((total, req) => {
      const itemsQuantity = req.items?.reduce((sum, item) => sum + (item.volume || 0), 0) || 0;
      return total + itemsQuantity;
    }, 0);
  };

  const calculateCustomerQuantities = (data: Requisition[]) => {
    return data.reduce((acc, req) => {
      const customerName = req.customer?.businessName || "Unknown";
      const itemsQuantity = req.items?.reduce((sum, item) => sum + (item.volume || 0), 0) || 0;
      acc[customerName] = (acc[customerName] || 0) + itemsQuantity;
      return acc;
    }, {} as Record<string, number>);
  };

  const filteredRequisitions = getRequisition?.filter((invoice: Requisition) => {
    if (searchField !== "combined" && !searchQuery) return true;
    if (searchField === "combined" && !businessQuery && !productQuery) return true;

    const businessNameMatch = invoice?.customer?.businessName
      ?.toLowerCase()
      .includes(searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase());
    const productNames = invoice?.items?.map((item) => item.name.toLowerCase()).join(" ") || "";
    const productNameMatch = productNames.includes(
      searchField === "combined" ? productQuery.toLowerCase() : searchQuery.toLowerCase()
    );

    switch (searchField) {
      case "businessName":
        return businessNameMatch;
      case "productName":
        return productNameMatch;
      case "combined":
        return businessNameMatch && productNameMatch;
      default:
        return businessNameMatch;
    }
  }) || [];

  const totalQuantity = calculateTotalQuantity(filteredRequisitions);
  const customerQuantities = calculateCustomerQuantities(filteredRequisitions);

  const paginatedRequisitions = filteredRequisitions.slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="board">
      <ToastContainer />
      <TopNav title="Loading Slip" />
      {reLogin ? <ReLoginModal /> : null}
      <div className="method loadingss">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select id="filter" value={searchField} onChange={(e) => setSearchField(e.target.value)}>
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <input
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <input
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
      </div>
      <div style={{float: "right"}}>
        <button
          onClick={handleMergeRequisitions}
          disabled={selectedRequisitions.length === 0}
          style={{ 
            margin: "10px", 
            padding: "8px 16px",
            opacity: selectedRequisitions.length === 0 ? 0.5 : 1,
            cursor: selectedRequisitions.length === 0 ? "not-allowed" : "pointer"
          }}
          className="merge-btn"
        >
          Merge & Create Loading Slip
        </button>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : (
          <>
            <table className="inTable">
              <thead>
                <tr className="heTable loT">
                  <th className="sn">
                    <input
                      type="checkbox"
                      checked={selectedRequisitions.length === getRequisition.length && getRequisition.length > 0}
                      onChange={() =>
                        setSelectedRequisitions(
                          selectedRequisitions.length === getRequisition.length
                            ? []
                            : getRequisition.map((req) => req._id)
                        )
                      }
                    />
                  </th>
                  <th className="sn">S/N</th>
                  <th>Customer Name</th>
                  <th>Product Name</th>
                  <th>Unit Price</th>
                  <th>Quantity</th>
                  <th>Quantity To Load</th>
                  <th>Total Cost</th>
                  <th>Date</th>
                  <th>Create Slip</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequisitions.length > 0 ? (
                  paginatedRequisitions.map((invoice: Requisition, inx: number) => {
                    const dateString = invoice.updatedAt;
                    const formattedDate = new Date(dateString).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit"
                    });
                    return (
                      <tr key={inx} className="taData">
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRequisitions.includes(invoice._id)}
                            onChange={() => handleSelectRequisition(invoice._id)}
                            style={{cursor:"pointer"}}
                          />
                        </td>
                        <td className="sn">{inx + 1}</td>
                        <td>{invoice?.customer?.businessName || "N/A"}</td>
                        <td>{invoice?.items?.map((des) => des.name).join(", ") || "N/A"}</td>
                        <td>{invoice?.items?.map((des) => des.unitPrice).join(", ") || "N/A"}</td>
                        <td>{invoice?.items?.map((des) => des.quantity).join(", ") || "N/A"}</td>
                        <td>{invoice?.items?.map((des) => des.volume).join(", ") || "N/A"}</td>
                        <td>{invoice?.items?.map((des) => des.totalCost).join(", ") || "N/A"}</td>
                        <td>{formattedDate}</td>
                        <td>
                          <button 
                            className="action-btn" 
                            onClick={() => handleCreateInvoice(invoice)}
                          >
                            Create Loading Slip
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredRequisitions.length > 0 && (
              <div className="aggregate">
                {Object.entries(customerQuantities).map(([customer, quantity]) => (
                  <div key={customer}>
                    <strong>{customer}: </strong>
                    {quantity}
                  </div>
                ))}
                <strong>Total Quantity to Load: </strong>
                {totalQuantity}
              </div>
            )}
          </>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredRequisitions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default LoadingSlipTable;